import React from "react";
import classnames from "classnames";
import { detect } from "detect-browser";

import {
  MdAutoAwesome,
  MdDarkMode,
  MdFileDownload,
  MdOpenInBrowser,
  MdHelpOutline,
  MdFindInPage,
  MdLanguage,
  MdLayers,
  MdSettings,
  MdLightMode,
  MdRedo,
  MdSave,
  MdUndo,
} from "react-icons/md";
import { withTranslation, WithTranslation } from "react-i18next";
import { supportedLanguages } from "../i18n";
import BrandMark from "./BrandMark";
import type { EditorMode, StudioTheme } from "../libs/preferences";

// This is required because of <https://stackoverflow.com/a/49846426>, there isn't another way to detect support that I'm aware of.
const browser = detect();
const colorAccessibilityFiltersEnabled =
  ["chrome", "firefox"].indexOf(browser!.name) > -1;

type IconTextProps = {
  children?: React.ReactNode;
};

class IconText extends React.Component<IconTextProps> {
  render() {
    return <span className="studio-icon-text">{this.props.children}</span>;
  }
}

type ToolbarLinkProps = {
  className?: string;
  children?: React.ReactNode;
  href?: string;
  // Icon-only links: shown as the tooltip and read as the accessible name.
  title?: string;
  onToggleModal?(...args: unknown[]): unknown;
};

class ToolbarLink extends React.Component<ToolbarLinkProps> {
  render() {
    return (
      <a
        className={classnames("studio-toolbar-link", this.props.className)}
        href={this.props.href}
        rel="noopener noreferrer"
        target="_blank"
        data-wd-key="toolbar:link"
        title={this.props.title}
        aria-label={this.props.title}
      >
        {this.props.children}
      </a>
    );
  }
}

type ToolbarSelectProps = {
  children?: React.ReactNode;
  wdKey?: string;
  // The select shows no label of its own; the wrapper carries the tooltip.
  title?: string;
};

class ToolbarSelect extends React.Component<ToolbarSelectProps> {
  render() {
    return (
      <div
        className="studio-toolbar-select"
        data-wd-key={this.props.wdKey}
        title={this.props.title}
      >
        {this.props.children}
      </div>
    );
  }
}

type ToolbarActionProps = {
  children?: React.ReactNode;
  onClick?(...args: unknown[]): unknown;
  wdKey?: string;
  // Shown as the tooltip and read as the accessible name. Required for
  // actions that are, or become on narrow screens, icon-only.
  title?: string;
  // The one filled button of the bar.
  primary?: boolean;
  // The text is hidden on narrow screens; the icon and the title stay.
  collapsible?: boolean;
};

class ToolbarAction extends React.Component<ToolbarActionProps> {
  render() {
    return (
      <button
        className={classnames("studio-toolbar-action", {
          "studio-toolbar-action--primary": this.props.primary,
          "studio-toolbar-action--collapsible": this.props.collapsible,
        })}
        data-wd-key={this.props.wdKey}
        onClick={this.props.onClick}
        title={this.props.title}
        aria-label={this.props.title}
      >
        {this.props.children}
      </button>
    );
  }
}

export type MapState =
  | "map"
  | "inspect"
  | "filter-achromatopsia"
  | "filter-deuteranopia"
  | "filter-protanopia"
  | "filter-tritanopia";

type AppToolbarInternalProps = {
  mapStyle: { name?: string };
  inspectModeEnabled: boolean;
  onStyleChanged(...args: unknown[]): unknown;
  // A new style has been uploaded
  onStyleOpen(...args: unknown[]): unknown;
  // A dict of source id's and the available source layers
  sources: object;
  children?: React.ReactNode;
  onToggleModal(...args: unknown[]): unknown;
  onSetMapState(mapState: MapState): unknown;
  mapState?: MapState;
  renderer?: string;
  editorMode: EditorMode;
  theme: StudioTheme;
  onSetEditorMode(mode: EditorMode): unknown;
  onToggleTheme(): unknown;
  onUndo(): unknown;
  onRedo(): unknown;
} & WithTranslation;

class AppToolbarInternal extends React.Component<AppToolbarInternalProps> {
  state = {
    isOpen: {
      settings: false,
      sources: false,
      open: false,
      add: false,
      export: false,
    },
  };

  handleSelection(val: MapState) {
    this.props.onSetMapState(val);
  }

  handleLanguageChange(val: string) {
    this.props.i18n.changeLanguage(val);
  }

  onSkip = (target: string) => {
    if (target === "map") {
      (
        document.querySelector(".maplibregl-canvas") as HTMLCanvasElement
      ).focus();
    } else {
      const el = document.querySelector(
        "#skip-target-" + target
      ) as HTMLButtonElement | null;
      el?.focus();
    }
  };

  render() {
    const t = this.props.t;
    const views = [
      {
        id: "map",
        group: "general",
        title: t("Map"),
      },
      {
        id: "inspect",
        group: "general",
        title: t("Inspect"),
        disabled: this.props.renderer === "ol",
      },
      {
        id: "filter-deuteranopia",
        group: "color-accessibility",
        title: t("Deuteranopia filter"),
        disabled: !colorAccessibilityFiltersEnabled,
      },
      {
        id: "filter-protanopia",
        group: "color-accessibility",
        title: t("Protanopia filter"),
        disabled: !colorAccessibilityFiltersEnabled,
      },
      {
        id: "filter-tritanopia",
        group: "color-accessibility",
        title: t("Tritanopia filter"),
        disabled: !colorAccessibilityFiltersEnabled,
      },
      {
        id: "filter-achromatopsia",
        group: "color-accessibility",
        title: t("Achromatopsia filter"),
        disabled: !colorAccessibilityFiltersEnabled,
      },
    ];

    const currentView = views.find((view) => {
      return view.id === this.props.mapState;
    });

    const styleName = this.props.mapStyle.name || t("Untitled style");

    return (
      <nav className="studio-toolbar">
        <div
          className="studio-toolbar__inner"
          role="navigation"
          aria-label="Toolbar"
        >
          <div className="studio-toolbar__zone studio-toolbar__zone--identity">
            <div className="studio-toolbar-logo-container">
              {/* Keyboard accessible quick links */}
              {/* The layer list and the layer editor only exist in Advanced. */}
              {this.props.editorMode !== "simple" && (
                <>
                  <button
                    data-wd-key="root:skip:layer-list"
                    className="studio-toolbar-skip"
                    onClick={(_e) => this.onSkip("layer-list")}
                  >
                    {t("Layers list")}
                  </button>
                  <button
                    data-wd-key="root:skip:layer-editor"
                    className="studio-toolbar-skip"
                    onClick={(_e) => this.onSkip("layer-editor")}
                  >
                    {t("Layer editor")}
                  </button>
                </>
              )}
              <button
                data-wd-key="root:skip:map-view"
                className="studio-toolbar-skip"
                onClick={(_e) => this.onSkip("map")}
              >
                {t("Map view")}
              </button>
              <a
                className="studio-toolbar-logo"
                target="blank"
                rel="noreferrer noopener"
                href="https://portal.mapmetrics.org/"
                aria-label={t("Atlas Studio")}
              >
                <BrandMark className="studio-toolbar-logo__mark" />
                {/* As the portal writes its name: one word, the second half bold. */}
                <span className="studio-toolbar-logo__name" aria-hidden="true">
                  Atlas<b>Studio</b>
                </span>
              </a>
            </div>
            <span
              className="studio-toolbar-style-name"
              data-wd-key="nav:style-name"
              title={styleName}
            >
              {styleName}
            </span>
            <div
              className="studio-toolbar-mode"
              role="group"
              aria-label={t("Editor mode")}
            >
              {(["simple", "advanced"] as EditorMode[]).map((mode) => (
                <button
                  key={mode}
                  className={classnames("studio-toolbar-mode__option", {
                    "studio-toolbar-mode__option--active":
                      this.props.editorMode === mode,
                  })}
                  aria-pressed={this.props.editorMode === mode}
                  data-wd-key={`nav:mode:${mode}`}
                  onClick={() => this.props.onSetEditorMode(mode)}
                >
                  {mode === "simple" ? t("Simple") : t("Advanced")}
                </button>
              ))}
            </div>
          </div>

          <div className="studio-toolbar__zone studio-toolbar__zone--edit">
            <ToolbarAction
              wdKey="nav:undo"
              onClick={this.props.onUndo}
              title={t("Undo")}
            >
              <MdUndo />
            </ToolbarAction>
            <ToolbarAction
              wdKey="nav:redo"
              onClick={this.props.onRedo}
              title={t("Redo")}
            >
              <MdRedo />
            </ToolbarAction>
            {this.props.editorMode === "simple" && (
              <ToolbarAction
                wdKey="nav:ai"
                onClick={this.props.onToggleModal.bind(this, "ai")}
                title={t("Edit with AI")}
                collapsible
              >
                <MdAutoAwesome />
                <IconText>{t("Edit with AI")}</IconText>
              </ToolbarAction>
            )}
          </div>

          <div className="studio-toolbar__zone studio-toolbar__zone--file">
            {/* Style-wide settings belong to Advanced mode; Simple mode keeps the bar short. */}
            {this.props.editorMode === "advanced" && (
              <div className="studio-toolbar__group">
                <ToolbarAction
                  wdKey="nav:sources"
                  onClick={this.props.onToggleModal.bind(this, "sources")}
                  title={t("Data Sources")}
                  collapsible
                >
                  <MdLayers />
                  <IconText>{t("Sources")}</IconText>
                </ToolbarAction>
                <ToolbarAction
                  wdKey="nav:settings"
                  onClick={this.props.onToggleModal.bind(this, "settings")}
                  title={t("Style Settings")}
                  collapsible
                >
                  <MdSettings />
                  <IconText>{t("Settings")}</IconText>
                </ToolbarAction>
              </div>
            )}
            <div className="studio-toolbar__group">
              <ToolbarAction
                wdKey="nav:open"
                onClick={this.props.onToggleModal.bind(this, "open")}
                title={t("Open")}
                collapsible
              >
                <MdOpenInBrowser />
                <IconText>{t("Open")}</IconText>
              </ToolbarAction>
              <ToolbarAction
                wdKey="nav:export"
                onClick={this.props.onToggleModal.bind(this, "export")}
                title={t("Export")}
                collapsible
              >
                <MdFileDownload />
                <IconText>{t("Export")}</IconText>
              </ToolbarAction>
              <ToolbarAction
                wdKey="nav:save"
                onClick={this.props.onToggleModal.bind(this, "save")}
                primary
              >
                <MdSave />
                <IconText>{t("Save")}</IconText>
              </ToolbarAction>
            </div>

            <span className="studio-toolbar__divider" aria-hidden="true" />

            <div className="studio-toolbar__group">
              <ToolbarSelect wdKey="nav:inspect" title={t("View")}>
                <MdFindInPage />
                <select
                  className="studio-select"
                  data-wd-key="studio-select"
                  aria-label={t("View")}
                  onChange={(e) =>
                    this.handleSelection(e.target.value as MapState)
                  }
                  value={currentView?.id}
                >
                  {views
                    .filter((v) => v.group === "general")
                    .map((item) => {
                      return (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={item.disabled}
                          data-wd-key={item.id}
                        >
                          {item.title}
                        </option>
                      );
                    })}
                  <optgroup label={t("Color accessibility")}>
                    {views
                      .filter((v) => v.group === "color-accessibility")
                      .map((item) => {
                        return (
                          <option
                            key={item.id}
                            value={item.id}
                            disabled={item.disabled}
                          >
                            {item.title}
                          </option>
                        );
                      })}
                  </optgroup>
                </select>
              </ToolbarSelect>

              <ToolbarSelect wdKey="nav:language" title={t("Language")}>
                <MdLanguage />
                <select
                  className="studio-select"
                  data-wd-key="studio-lang-select"
                  aria-label={t("Language")}
                  onChange={(e) => this.handleLanguageChange(e.target.value)}
                  value={this.props.i18n.language}
                >
                  {Object.entries(supportedLanguages).map(([code, name]) => {
                    return (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </ToolbarSelect>

              <ToolbarAction
                wdKey="nav:theme"
                onClick={this.props.onToggleTheme}
                title={this.props.theme === "dark" ? t("Light") : t("Dark")}
              >
                {this.props.theme === "dark" ? <MdLightMode /> : <MdDarkMode />}
              </ToolbarAction>
              <ToolbarLink
                href={"https://docs.mapatlas.xyz/"}
                title={t("Help")}
              >
                <MdHelpOutline />
              </ToolbarLink>
            </div>
          </div>
        </div>
      </nav>
    );
  }
}

const AppToolbar = withTranslation()(AppToolbarInternal);
export default AppToolbar;
